import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { Box, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";

import { PrimaryButton } from "./CustomButton";
import { CustomTileContainer } from "./CustomTile";

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        position: "relative",
        flex: 1,
        minHeight: 0,
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    content: {
        flex: 1,
        minHeight: 0,
        width: "100%",
        overflow: "hidden",
        pointerEvents: "none",
    },
    blur: { filter: "blur(5px)" },
    interactionBlocker: {
        position: "fixed",
        inset: 0,
        zIndex: 50,
        pointerEvents: "auto",
    },
    cardOverlay: {
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        pointerEvents: "none",
    },
    expiredCard: {
        display: "flex",
        flexDirection: "column",
        padding: theme.spacing(8),
        gap: theme.spacing(2),
        maxWidth: 400,
        alignItems: "center",
        pointerEvents: "auto",
    },
}));

export const ExpiredWrapper = (props: React.PropsWithChildren<object>) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["common"]);

    const isExpired = useCurrentSubscriptionExpired();

    const onRenew = () => {
        window.open("https://emteria.com/p/subscription", "_blank");
    };

    if (!isExpired) {
        return <React.Fragment>{props.children}</React.Fragment>;
    }

    return (
        <Box className={classes.container}>
            <Box className={cx(classes.content, classes.blur)}>{props.children}</Box>
            <Box className={classes.interactionBlocker} aria-hidden />
            <Box className={classes.cardOverlay}>
                <CustomTileContainer className={classes.expiredCard}>
                    <Typography variant="h6" style={{ fontWeight: "bold" }}>
                        {t("common:section.expiredWrapper.title")}
                    </Typography>
                    <Typography align="center">{t("common:section.expiredWrapper.description")}</Typography>
                    <PrimaryButton onClick={onRenew}>{t("common:section.expiredWrapper.button")}</PrimaryButton>
                </CustomTileContainer>
            </Box>
        </Box>
    );
};

export const withExpiredWrapper = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
    const ComponentWithExpiredWrapper: React.FC<P & React.PropsWithChildren<object>> = (props) => {
        const isExpired = useCurrentSubscriptionExpired();

        if (!isExpired) {
            return <WrappedComponent {...props} />;
        }

        return (
            <ExpiredWrapper>
                <WrappedComponent {...props} />
            </ExpiredWrapper>
        );
    };

    return ComponentWithExpiredWrapper;
};
